import Signature from './AbstractSignature';
import { CreateElement, VNode } from 'vue';
import { ChildParams, MethodAttributes, referencesMethod } from '@/templates/References';

export default function SwiftSignatureFactory(kind: string) {
  switch (kind) {
    case 'method':
      return SwiftMethodSignature;
    case 'prop':
      return SwiftPropertySignature;
    case 'typedef':
      return SwiftTypedefSignature;
    default:
      return {
        getSignature(_method: any): null {
          return null;
        },
      };
  }
}

class SwiftMethodSignature extends Signature {
  public static LANG = 'swift';

  public static getSignature(method: referencesMethod): (h: CreateElement) => VNode {
    const attrStatic = this.attrStatic(method.attributes);
    const funcTitle = this.funcTitle(<string>method.title);
    const paramSignatures = this.paramSignatures(
      this.getParamNames(<string>method.title),
      method.params
    );
    const returnsType = this.returnsType(method.returns);

    return (h) =>
      h('code', [
        h('span', { class: 'token punctuation' }, `${attrStatic} `),
        h('span', { class: 'token function' }, 'func '),
        `${funcTitle}`,
        h('span', { class: 'token punctuation' }, '('),
        paramSignatures(h),
        h('span', { class: 'token punctuation' }, ') '),
        h('span', { class: 'token punctuation' }, '-> '),
        returnsType(h),
      ]);
  }

  private static attrStatic(attributes: MethodAttributes): string {
    return attributes && attributes.static ? 'class' : '';
  }

  /**
   * Имя функции записано в title до первой открывающей скобки
   * @param title тайтл метода method.title
   * @return string строка, часть сигнатуры
   */
  private static funcTitle(title: string): string {
    const a: null | string[] = title.match(/^.+?(?=\()/g);
    return !a ? '' : a[0];
  }

  /**
   * Имена параметров записаны в title (между скобок через ':') создаем из них массив
   * @param title тайтл метода method.title
   * @return Array массив содержащий имена параметров
   */
  private static getParamNames(title: string): string[] {
    const a: null | string[] = title.match(/\((.*?)\)/);
    return !a ? [] : a[1].split(':').filter((str) => str);
  }

  /**
   * Проходимся по массиву имен параметров,
   * каждому сопоставляем параметр из массива params и записываем в строку следующего вида:
   * withConnectivityCheck connectivityCheck: Bool
   *
   * @param paramNames массив содержащий имена параметров созданный на основе method.title
   * @param paramSignatures массив содержащий параметры метода заданные в method.params
   * @return Function возвращает рендер функцию, которая создает часть сигнатуры
   */
  private static paramSignatures(
    paramNames: string[],
    paramSignatures: ChildParams[]
  ): (h: CreateElement) => VNode[] {
    return (h) =>
      paramNames.map((paramName: string, idx: number) => {
        if (!paramSignatures) {
          h('span', `${paramName}`);
        }
        return paramName === paramSignatures[idx].title
          ? h('span', [
              `${paramName}`,
              h('span', { class: 'token punctuation' }, ': '),
              h('span', {
                class: 'token md-parse',
                domProps: {
                  innerHTML: `${
                    paramSignatures[idx].types
                      ? this.convertToHtml(<string>paramSignatures[idx].types[0])
                      : ''
                  }`,
                },
              }),
              !(idx === paramNames.length - 1)
                ? h('span', { class: 'token punctuation' }, ', ')
                : '',
            ])
          : h('span', [
              `${paramName} `,
              `${paramSignatures[idx].title}`,
              h('span', { class: 'token punctuation' }, ': '),
              h('span', {
                class: 'token md-parse',
                domProps: {
                  innerHTML: `${
                    paramSignatures[idx].types
                      ? this.convertToHtml(<string>paramSignatures[idx].types[0])
                      : ''
                  }`,
                },
              }),
              !(idx === paramNames.length - 1)
                ? h('span', { class: 'token punctuation' }, ', ')
                : '',
            ]);
      });
  }
}

class SwiftPropertySignature extends Signature {
  public static LANG = 'swift';

  //TODO сделать тип для property
  public static getSignature(property: any): (h: CreateElement) => VNode {
    const title = property.title;
    const attrReadonly = this.attrReadonly(property.attributes);
    const returnsType = this.returnsType(property.returns);
    return (h) =>
      h('code', [
        h('span', { class: 'token function' }, 'var '),
        `${title}`,
        h('span', { class: 'token punctuation' }, ': '),
        returnsType(h),
        attrReadonly(h),
      ]);
  }

  private static attrReadonly(attributes: any): (h: CreateElement) => VNode {
    return attributes && attributes.readonly
      ? (h) =>
          h('span', [
            h('span', { class: 'token punctuation' }, ' {'),
            'get',
            h('span', { class: 'token punctuation' }, '}'),
          ])
      : (h) => h();
  }
}

class SwiftTypedefSignature extends Signature {
  public static LANG = 'swift';

  public static getSignature(typedef: referencesMethod): (h: CreateElement) => VNode {
    const paramSignatures = this.paramSignatures(typedef.params);
    const returnsType = this.returnsType(typedef.returns);

    return (h) =>
      h('code', [
        h('span', { class: 'token punctuation' }, 'typealias '),
        `${typedef.title} `,
        h('span', { class: 'token punctuation' }, '= '),
        h('span', { class: 'token punctuation' }, '('),
        paramSignatures(h),
        h('span', { class: 'token punctuation' }, ') '),
        h('span', { class: 'token punctuation' }, '-> '),
        returnsType(h),
      ]);
  }

  private static paramSignatures(paramSignatures: ChildParams[]): (h: CreateElement) => VNode {
    if (!paramSignatures) return (h) => h();

    return (h) =>
      h(
        'span',
        paramSignatures.map((val: ChildParams, idx: number) => {
          return h('span', [
            `${val.title}`,
            h('span', { class: 'token punctuation' }, ': '),

            val.types ? this.returnsType(val.types)(h) : '',

            !(idx === paramSignatures.length - 1)
              ? h('span', { class: 'token punctuation' }, ', ')
              : '',
          ]);
        })
      );
  }
}
